import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { usePaymentsDialog as usePaymentsDialogRoot } from 'payments/components/payments-dialog-wrapper-context'
import type { DefaultPlan } from 'payments/payments-dialog/lib/plans'
import type { UseCase } from 'cf-types'
import {
  PAYMENT_ENTRY_POINTS,
  savePaymentModalEntryPoint,
} from 'payments/payments-dialog/analytics'
import { useIsLoggedInQuery, userAuthQueryKey } from 'auth/hooks'
import { isLoggedInQuerySelector } from 'auth/selectors'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'
import {
  OPEN_PAYMENT_DIALOG_EVENT_NAME,
  parseOpenPaymentsDialogPlan,
  parseOpenPaymentsDialogUseCase,
} from '../open-payment-dialog-event'
import type { OpenPaymentsDialogEventDetail } from '../open-payment-dialog-event'
import { TRIAL_CTA_VARIANTS } from '../features'
import { isDefaultStudioAiPlan, pricingPageHref } from '../trial-cta'
import { useReadTrialCtaVariant } from './use-trial-cta-variant'

export function usePaymentsDialog() {
  const queryClient = useQueryClient()
  const loginModal = useUserAuthModal()
  const paymentsModal = usePaymentsDialogRoot()
  const readTrialCtaVariant = useReadTrialCtaVariant()
  useIsLoggedInQuery()

  const open = React.useCallback(
    (plan?: DefaultPlan, useCase: UseCase = 'purchase') => {
      // Read auth state at call time — success callbacks can run before this hook re-renders
      // with the updated login status after registration.
      const isLoggedInNow = isLoggedInQuerySelector(queryClient.getQueryData(userAuthQueryKey))

      if (!isLoggedInNow) {
        loginModal.openModal()
        return
      }

      if (isDefaultStudioAiPlan(plan) && readTrialCtaVariant() === TRIAL_CTA_VARIANTS.pricingPage) {
        // A full page load, because hosts mount the dialog-opening event listener outside their router.
        window.location.assign(pricingPageHref(window.location.pathname))
        return
      }

      const defaultPlan = plan || 'aiStudio'
      savePaymentModalEntryPoint(PAYMENT_ENTRY_POINTS.STUDIO)
      paymentsModal.open(defaultPlan, useCase)
    },
    [paymentsModal, loginModal, queryClient, readTrialCtaVariant],
  )
  return { open }
}

export function useOpenPaymentsDialogFromEvent() {
  const { open } = usePaymentsDialog()

  React.useEffect(() => {
    const handler: EventListener = event => {
      const { detail } = event as CustomEvent<OpenPaymentsDialogEventDetail>
      open(
        parseOpenPaymentsDialogPlan(detail?.plan),
        parseOpenPaymentsDialogUseCase(detail?.useCase) ?? 'purchase',
      )
    }

    document.addEventListener(OPEN_PAYMENT_DIALOG_EVENT_NAME, handler)

    return () => {
      document.removeEventListener(OPEN_PAYMENT_DIALOG_EVENT_NAME, handler)
    }
  }, [open])
}
